import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'convex/react';
import { api } from '../../convex/_generated/api';
import Layout from '../components/Layout/Layout';

interface InteractionSummary {
  action: string;
  total: number;
  uniqueSessions: number;
  uniqueUsers: number;
}

interface Interaction {
  action: string;
  count: number;
  unique_sessions: number;
  unique_users: number;
}

interface InterestSubmission {
  _id: string;
  email: string;
  name?: string;
  mobileNumber?: string;
  telephoneNumber?: string;
  createdAt: number;
  updatedUserDetails: boolean;
  user?: {
    username: string;
    lastLogin?: number;
  } | null;
  status: string;
}

interface InterestedAccount {
  id: string;
  email: string;
  name: string | null;
  mobile_number: string | null;
  telephone_number: string | null;
  created_at: number;
  updated_user_details: boolean;
  username: string | null;
  last_login: number | null;
  status: 'Registered User' | 'Prospect';
}

const SecretAnalyticsPage: React.FC = () => {
  const navigate = useNavigate();

  // Convex queries
  const interactionSummary = useQuery(api.analytics.getPopoutSummary);
  const interestStats = useQuery(api.analytics.getInterestStats);
  const userStats = useQuery(api.analytics.getUserStats);
  const interestSubmissions = useQuery(api.analytics.getInterestSubmissions, { limit: 100 });

  const loading =
    interactionSummary === undefined ||
    interestStats === undefined ||
    userStats === undefined;

  const getActionLabel = (action: string) => {
    const labels: { [key: string]: string } = {
      'view': 'Page Views',
      'click_tab': 'Tab Clicks',
      'open': 'Form Opens',
      'close': 'Popout Closes',
      'submit': 'Form Submissions'
    };
    return labels[action] || action;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-lg">Loading analytics...</div>
        </div>
      </Layout>
    );
  }

  // Build the analytics object from Convex queries
  const analytics = {
    interactions: interactionSummary?.map((i: InteractionSummary) => ({
      action: i.action,
      count: i.total,
      unique_sessions: i.uniqueSessions,
      unique_users: i.uniqueUsers
    })) || [],
    signups: {
      total_signups: interestStats?.totalSignups || 0,
      unique_emails: interestStats?.uniqueEmails || 0,
      details_updated: interestStats?.detailsUpdated || 0
    },
    userStats: {
      total_users: userStats?.totalUsers || 0,
      recent_users: userStats?.recentUsers || 0,
      recent_users_percentage: userStats?.recentUsersPercentage || '0'
    }
  };

  // Transform interested accounts
  const interestedAccounts: InterestedAccount[] = interestSubmissions?.interests.map((interest: InterestSubmission) => ({
    id: interest._id,
    email: interest.email,
    name: interest.name || null,
    mobile_number: interest.mobileNumber || null,
    telephone_number: interest.telephoneNumber || null,
    created_at: interest.createdAt,
    updated_user_details: interest.updatedUserDetails,
    username: interest.user?.username || null,
    last_login: interest.user?.lastLogin || null,
    status: interest.status as 'Registered User' | 'Prospect'
  })) || [];
  const interestSubmissionsLoading = interestSubmissions === undefined;
  const submittedDetailsCount = interestSubmissions?.total ?? 0;

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-light">Secret Campaign Analytics</h1>
          <button
            onClick={() => navigate('/admin')}
            className="text-gray-600 hover:text-gray-900"
          >
            ← Back to Admin
          </button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-medium font-sans text-gray-700 mb-2">Total Signups</h3>
            <p className="text-3xl font-bold text-purple-600">
              {analytics.signups.total_signups}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              {analytics.signups.unique_emails} unique emails
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-medium font-sans text-gray-700 mb-2">Conversion Rate</h3>
            <p className="text-3xl font-bold text-green-600">
              {analytics.interactions.length > 0 ? (
                ((analytics.signups.total_signups /
                  (analytics.interactions.find((i: Interaction) => i.action === 'view')?.unique_sessions || 1)) * 100).toFixed(1)
              ) : '0'}%
            </p>
            <p className="text-sm text-gray-500 mt-1">
              From views to signups
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-medium font-sans text-gray-700 mb-2">Total Users</h3>
            <p className="text-3xl font-bold text-indigo-600">
              {analytics.userStats.total_users}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              {analytics.userStats.recent_users} active ({analytics.userStats.recent_users_percentage}%)
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-medium font-sans text-gray-700 mb-2">Details Updated</h3>
            <p className="text-3xl font-bold text-blue-600">
              {analytics.signups.details_updated}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              Users who updated their info
            </p>
          </div>
        </div>

        {/* Interaction Breakdown */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-xl font-medium font-sans mb-4">User Interactions (Last 30 Days)</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4">Action</th>
                  <th className="text-right py-3 px-4">Total Count</th>
                  <th className="text-right py-3 px-4">Unique Sessions</th>
                  <th className="text-right py-3 px-4">Unique Users</th>
                </tr>
              </thead>
              <tbody>
                {analytics.interactions.map((interaction: Interaction) => (
                  <tr key={interaction.action} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-4">{getActionLabel(interaction.action)}</td>
                    <td className="text-right py-3 px-4">{interaction.count}</td>
                    <td className="text-right py-3 px-4">{interaction.unique_sessions}</td>
                    <td className="text-right py-3 px-4">{interaction.unique_users || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Funnel Visualization */}
        <div className="bg-white rounded-lg shadow-sm p-6 mt-6">
          <h2 className="text-xl font-medium font-sans mb-4">Conversion Funnel</h2>
          <div className="space-y-4">
            {['view', 'click_tab', 'open', 'submit'].map((action) => {
              const interaction = analytics.interactions.find((i: Interaction) => i.action === action);
              const sessions = interaction?.unique_sessions || 0;
              const maxSessions = analytics.interactions.find((i: Interaction) => i.action === 'view')?.unique_sessions || 1;
              const percentage = (sessions / maxSessions) * 100;

              return (
                <div key={action}>
                  <div className="flex justify-between text-sm mb-1">
                    <span>{getActionLabel(action)}</span>
                    <span>{sessions} ({percentage.toFixed(1)}%)</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-4">
                    <div
                      className="bg-purple-600 h-4 rounded-full transition-all duration-500"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Interested Accounts Section */}
        <div className="bg-white rounded-lg shadow-sm p-6 mt-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-xl font-medium font-sans">Interested Accounts</h2>
              <p className="text-sm text-gray-500 mt-1">
                {interestSubmissionsLoading ? 'Loading submitted details...' : `${submittedDetailsCount} submitted details`}
              </p>
            </div>
          </div>

          <div className="overflow-x-auto">
            {interestSubmissionsLoading ? (
              <div className="text-center py-8 text-gray-600">Loading accounts...</div>
            ) : interestedAccounts.length === 0 ? (
              <div className="text-center py-8 text-gray-600">No submitted details yet.</div>
            ) : (
              <table className="min-w-full">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left py-3 px-4">Email</th>
                    <th className="text-left py-3 px-4">Name</th>
                    <th className="text-left py-3 px-4">Phone</th>
                    <th className="text-left py-3 px-4">Status</th>
                    <th className="text-left py-3 px-4">Updated Account</th>
                    <th className="text-left py-3 px-4">Username</th>
                    <th className="text-left py-3 px-4">Last Login</th>
                    <th className="text-left py-3 px-4">Signup Date</th>
                  </tr>
                </thead>
                <tbody>
                  {interestedAccounts.map((account: InterestedAccount) => (
                    <tr key={account.id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4">{account.email}</td>
                      <td className="py-3 px-4">{account.name || '-'}</td>
                      <td className="py-3 px-4">
                        {account.mobile_number || account.telephone_number || '-'}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          account.status === 'Registered User'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {account.status}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        {account.updated_user_details ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Yes
                          </span>
                        ) : (
                          <span className="text-gray-500">No</span>
                        )}
                      </td>
                      <td className="py-3 px-4">{account.username || '-'}</td>
                      <td className="py-3 px-4">
                        {account.last_login
                          ? new Date(account.last_login).toLocaleDateString()
                          : '-'}
                      </td>
                      <td className="py-3 px-4">
                        {new Date(account.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default SecretAnalyticsPage;
